import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { ImageComponent } from 'common-ui';
import { ProductCategoryComponentStore, ProductCategoryTreeNodeModel } from 'products-data';

import { NestedTreeControl } from '@angular/cdk/tree';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { parseQuery } from '@idealsupply/query-parser-ts';
import { map } from 'rxjs';

@Component({
  selector: 'ideal-legacy-product-category-nav',
  imports: [MatTreeModule, MatIconModule, MatButtonModule, NgTemplateOutlet, ImageComponent, RouterModule],
  templateUrl: './legacy-product-category-nav.component.html',
  styleUrl: './legacy-product-category-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ProductCategoryComponentStore],
  host: {
    '[class.loading]': '!categoriesAreReady()',
  },
})
export class LegacyProductCategoryNavComponent {
  private _store = inject(ProductCategoryComponentStore);
  private _route = inject(ActivatedRoute);

  protected readonly categories = this._store.categories;
  protected readonly categoriesAreReady = computed(() => !this._store.categories.loading() && this._store.categoryTree() != null);
  protected readonly categoriesAreLoading = computed(() => this._store.categories.loading());
  protected readonly query = toSignal(this._route.queryParams.pipe(map((params) => (params['q'] as string) || '')), {
    initialValue: (this._route.snapshot.queryParams['q'] as string) || '',
  });

  protected readonly categoryCondition = computed(() => {
    const parsedQuery = parseQuery(this.query());
    if (parsedQuery.success) {
      if (parsedQuery.ast.type === 'condition' && parsedQuery.ast.field === 'category') {
        return parsedQuery.ast.value as string;
      }
    }
    return undefined;
  });

  protected hasChildren = (_: number, node: ProductCategoryTreeNodeModel) => {
    return node.children != null && node.children.length > 0;
  };

  protected readonly treeControl = new NestedTreeControl<ProductCategoryTreeNodeModel>((node) => node.children);
  protected readonly dataSource = new MatTreeNestedDataSource<ProductCategoryTreeNodeModel>();

  protected categoryParams(node: ProductCategoryTreeNodeModel) {
    return {
      q: `category='${node.id}'`,
    };
  }

  protected isSelected(node: ProductCategoryTreeNodeModel) {
    return this.categoryCondition() === node.id;
  }

  protected isChildSelected(node: ProductCategoryTreeNodeModel) {
    if (!node.children) {
      return false;
    }
    return this.categoryCondition()?.startsWith(`${node.id}.`) ?? false;
  }

  protected isExpandable(node: ProductCategoryTreeNodeModel) {
    return this.hasChildren(0, node);
  }

  protected isExpanded(node: ProductCategoryTreeNodeModel) {
    if (!this.hasChildren(0, node)) {
      return false;
    }

    return this.treeControl.isExpanded(node);
  }

  constructor() {
    effect(() => {
      this.dataSource.data = this._store.categoryTree();
    });
  }
}
